import { makeAutoObservable } from 'mobx';
import { PopupConfig } from 'components/popup/popup-config';
import { PopupLayout } from 'components/popup/popup.store';
import { CacheAreaKey } from 'core/config/cache-config';
import { AnalyticsEventJourneyKey, AnalyticsJourneyKey } from 'core/enums/analytics-journey-key';
import { ClaimCard, ClaimOutcome, RewardStatus } from 'core/enums/rewards';
import { RewardLevel } from 'core/enums/rewards-levels';
import { Product } from 'core/models/product/product';
import { ClaimHistoryResponse } from 'core/models/red-oxygen/claim-history';
import { RedOxygenGetTierInfo, RewardBase } from 'core/models/red-oxygen/get-tier';
import { RedOxygenErrorResponse, RedOxygenGenericResponse } from 'core/models/red-oxygen/red-oxygen';
import { StarCalc } from 'core/models/red-oxygen/star-calc';
import { CompletedCompetition, ContentWithImage, LandingPageContent, Reward, RewardCard, RewardQueryParams, StarCategories, WinnersCircleContent } from 'core/models/rewards/rewards';
import { RedOxygenService } from 'core/services/api/red-oxygen';
import { RewardsService } from 'core/services/api/rewards';
import { SubscriptionService } from 'core/services/api/subscriptions';
import { REWARD_LEVELS } from 'core/services/mock/rewards';
import { StorageHelper } from 'core/services/storage';
import { AnalyticsEventStore } from 'core/stores/analytics-events.store';
import { CurrencyStore } from 'core/stores/currency.store';
import { Util } from 'utils/common.utils';
import { RewardUtils } from 'utils/reward.utils';

export const joinNowBkgImg = 'rewards/Header_Image_Rewards_Landing16x9.webp'; //'rewards/Header_Image_16x9.webp';

const debounceRewardsStorageArea = () => {
  const storageHelper = StorageHelper.GetInstance;
  storageHelper.debounceStorageArea('req', CacheAreaKey.Rewards);
  storageHelper.debounceStorageArea('req', CacheAreaKey.RewardsHistory);
};

export class RewardsStore {
  agreementId!: number;
  queryParams!: RewardQueryParams;
  isInit = false;
  isInitContent = false;
  compEntries = 0;
  rewardsClaimed = 0;
  starsCount = 0;
  value = 0;
  isDetailsWindowOpen = false;
  isLoading = true;
  isLoadingClaim = false;
  isJoined = false;
  noOfEntries = '';
  currencyStore!: CurrencyStore;
  principalPackage!: string;
  hasError = false;
  errorMsg?: string | null;

  optInImage = 'rewards/rewards-leadlanding.webp';
  landingBackgroundImage = 'rewards/Header_Reward_Image_Plain.png';
  bannerImage = 'rewards/rewards-banner.png';
  premiumExperienceImage = 'rewards/premium-experience.png';

  rewardLevels = REWARD_LEVELS;

  rewardTypes: RewardCard[] = [];
  claimableRewards: RewardCard[] = [];
  claimableRewardsLocked: RewardCard[] = [];
  rewardHistoryCardsAll: RewardCard[] = [];
  rewardHistoryCardsClaimed: RewardCard[] = [];
  rewardHistoryCardsEntered: RewardCard[] = [];
  rewardHistoryCardsMissed: RewardCard[] = [];
  landingPageContent: LandingPageContent = {} as LandingPageContent;
  competitionsContent: ContentWithImage[] = [];
  winnersCircleContent: WinnersCircleContent = {} as WinnersCircleContent;
  competitionReward!: RewardBase;
  competitionCategory: string = '';
  rewardCompetitionCards: RewardCard[] = [];
  winnersCircle: CompletedCompetition[] = [];
  competitionCategories: string[] = [];
  earnMoreStars = [] as Partial<RewardCard>[];
  starStatBase!: StarCategories;
  earnedStars = [] as Partial<RewardCard>[];
  itemDetails = {} as RewardCard;
  earnedStarsStats = {} as StarCalc;
  maxStarsStats = {} as StarCalc;
  claimOutcome!: ClaimOutcome;
  rewardsLevel!: RewardLevel;
  nextLevel!: RewardLevel;
  numberOfCoins = 0;

  isPremiumExperienceTier = false;

  popupConfig: PopupConfig = {
    template: PopupLayout.PremiumExperiences,
    hasCancelButton: true,
    hideCloseIcon: true,
  };

  constructor() {
    makeAutoObservable(this);
  }

  //A store shouldn't be doing this - Single Responsibility...
  initialize(customerNumber: string, countryCodeThreeChar: string, countryName: string, currencyStore: CurrencyStore) {
    this.changeIsDetailsWindowOpen(false);

    if (this.isInit) {
      return;
    }

    this.changeErrorMsg(null);
    this.changeQueryParams({ customerNumber, countryCodeThreeChar });
    this.changeIsLoading(true);
    this.changeCurrencyStore(currencyStore);

    Promise.all([
      RedOxygenService.getTierInfo(customerNumber),
      RedOxygenService.getClaimHistory(customerNumber),
      RedOxygenService.getGeneralExperience(customerNumber),
      RewardsService.getRewardsContent(),
      SubscriptionService.getSubscriptions(customerNumber, countryName),
    ])
      .then(([rewardsData, rewardHistoryData, generalExperienceData, contentResponse, subscriptions]) => {
        this.changeIsInit(true);
        this.changeHasError(false);
        this.changeErrorMsg(null);

        const highestPackage = Product.FromSubscriptionDTOArray(subscriptions).find(
          (sub) => sub.isHighestPackage === 'Yes'
        ) as Product;

        const error = this.getAnyError([rewardsData, rewardHistoryData, generalExperienceData]);
        if (error) {
          this.changeErrorMsg(error?.problemDetails);
          return;
        }

        let promoRewards: Reward[] = [];

        if (contentResponse) {
          const { content, winnersCircle, earnStars, promos } = contentResponse;

          promoRewards = promos;
          this.changeWinnersCircle(winnersCircle);
          this.changeLandingPageContent(content?.rewards);
          this.changeCompetitionsContent(content?.competitions);
          this.changeWinnersCircleContent(content?.winnersCircle);
          this.changeRewardTypes(RewardUtils.MapRewardContent(content?.rewards.rewardTypes));

          //Process rewards before earn stars so that stats is set
          this._processRewards(rewardsData, promoRewards);
          this._processEarnStars(earnStars);
        }

        this.changeAgreementId(highestPackage?.agreementId || 0);
        this.changePrincipalPackage(highestPackage?.serviceDescription || '');

        this._processHistoricalData(rewardHistoryData, promoRewards);

        if (generalExperienceData) {
          const coins = generalExperienceData.values.currencies.dstvCoins;
          this.changeNumberOfCoins(coins);
        }
      })
      .catch((err) => {
        console.error(err);
        this.changeHasError(true);
        this.changeErrorMsg('An error occurred during the request.');
      })
      .finally(() => {
        this.changeIsLoading(false);
      });
  }

  initializeContent() {
    this.changeIsDetailsWindowOpen(false);

    if (this.isInitContent) {
      return;
    }

    this.changeErrorMsg(null);
    this.changeIsLoading(true);

    RewardsService.getRewardsContent()
      .then((contentResponse) => {
        if (!contentResponse) {
          this.changeHasError(true);
          return;
        }
        this.changeIsInit(true);
        this.changeHasError(false);
        this.changeErrorMsg(null);

        const { content, winnersCircle, earnStars } = contentResponse;

        this.changeWinnersCircle(winnersCircle);
        this.changeLandingPageContent(content?.rewards);
        this.changeCompetitionsContent(content?.competitions);
        this.changeWinnersCircleContent(content?.winnersCircle);
        this.changeRewardTypes(RewardUtils.MapRewardContent(content?.rewards.rewardTypes));
        //Process rewards before earn stars so that stats is set
        this._processEarnStars(earnStars);
      })
      .catch((err) => {
        console.error(err);
        this.changeHasError(true);
        this.changeErrorMsg('An error occurred during the request.');
      })
      .finally(() => {
        this.changeIsInitContent(true);
        this.changeIsLoading(false);
      });
  }

  private _processEarnStars(earnStars: Reward[]) {
    const allEarnStars = RewardUtils.MapStatCalculations(earnStars, this.earnedStarsStats, this.maxStarsStats);

    const aggregatedStars = [
      ...allEarnStars.productUsage.items,
      ...allEarnStars.selfServiceUsage.items,
      ...allEarnStars.stayConnected.items,
      ...allEarnStars.other.items,
      ...allEarnStars.watchTime.items,
    ];

    const canEarn = aggregatedStars.filter((x) => !x.item.achieved && !x.item.disabled);
    const earned = aggregatedStars.filter((x) => x.item.achieved);

    this.changeStarStatBase(allEarnStars);

    this.changeEarnMoreStars(canEarn);

    this.changeEarnedStars(earned);
  }

  private _processRewards(rewardsData: RedOxygenGetTierInfo, rewardsContent: Reward[]) {
    const defaultStarCalc = {} as StarCalc;

    const { stats, tier, nextTier, activeCompetitions, rewardsMeta, competitionsMeta } = rewardsData;
    this.changeIsPremiumExperienceTier(tier.name === 'Tier 4' && !!tier.maxCalc.onPremium);

    this.changeEarnedStarsStats(tier?.calc ?? defaultStarCalc);
    this.changeMaxStarsStats(tier?.maxCalc ?? defaultStarCalc);

    this.changeIsJoined(stats.optin);
    this.changeStarsCount(stats.points);
    this.changeRewardsLevel(this.starsCount);
    this.changeNextLevel(this.starsCount);

    const tierRewards = tier?.rewards?.length ? tier.rewards.filter((reward) => reward.status === 'Available') : null;

    const nextTierRewards = nextTier?.rewards?.length ? nextTier.rewards : null;

    const activeCompetitionRewards = activeCompetitions?.length ? activeCompetitions : null;

    const map = RewardUtils.MapRewardContentAndData;

    if (tierRewards) {
      const competitionReward =
        tierRewards.find(({ type }) => type === 'Competition Entries Reward') || ({} as RewardBase);

      this.changeCompetitionReward(competitionReward);
      this.changeCompEntries(competitionReward.count);

      const mappedData = map(tierRewards, rewardsContent, this.rewardsLevel, undefined, rewardsMeta);
      this.changeClaimableRewards(mappedData);
    }

    if (nextTierRewards) {
      const mappedData = map(nextTierRewards, rewardsContent, undefined, false);
      this.changeClaimableRewardsLocked(mappedData);
    }

    if (activeCompetitionRewards) {
      const mappedData = map(activeCompetitionRewards, rewardsContent, this.rewardsLevel, undefined, competitionsMeta);
      this.changeRewardCompetitionCards(mappedData);
    }
  }

  private _processHistoricalData(rewardHistoryData: ClaimHistoryResponse, rewardsContent: Reward[]) {
    if (!rewardHistoryData) {
      return;
    }

    const rewards = Object.values(rewardHistoryData?.rewards || {});

    const competitions = RewardUtils.GetReducedCompetitionDuplicates(rewardHistoryData.competitions);

    const competitionCards: RewardCard[] = this.rewardCompetitionCards.map((item: RewardCard) => {
      const matchingCompetition = competitions.find((competition) => competition.competitionName === item.name);

      return {
        ...item,
        description: {
          ...item.description,
          noOfEntries: matchingCompetition?.entriesCount,
        },
      };
    });

    const sortedHistory = RewardUtils.SortRewardsByDate([...competitions, ...rewards]);

    this.changeRewardCompetitionCards(competitionCards);
    const claimedRewards = rewards.filter((card) => card.status === RewardStatus.Claimed);
    // NOTE: The two values below can not be extracted from the mapped data, since not all claimed rewards have their Umbraco match
    this.changeRewardHistoryCardsClaimed(
      RewardUtils.MapRewardContentAndData(claimedRewards, rewardsContent, this.rewardsLevel)
    );
    this.changeRewardsClaimedNumber(claimedRewards.length);
    this.changeValue(claimedRewards.reduce((total, reward) => total + parseInt(reward.value || '0'), 0));
    this.changeRewardHistoryCardsAll(
      RewardUtils.MapRewardContentAndData(sortedHistory, rewardsContent, this.rewardsLevel, false)
    );
  }

  //POST METHODS
  optIntoRewards() {
    const { customerNumber } = this.queryParams;
    const promise = RedOxygenService.optIntoRewards(customerNumber);

    this.handleRewardsRequest(promise, true);
  }

  optOutOfRewards() {
    const { customerNumber } = this.queryParams;
    const promise = RedOxygenService.optOutOfRewards(customerNumber);

    this.handleRewardsRequest(promise, false);
  }

  handleRewardsRequest(promise: Promise<RedOxygenErrorResponse | RedOxygenGenericResponse>, optIn: boolean) {
    this.changeIsLoadingClaim(true);

    promise
      .then((data) => {
        const error = this.getError(data);
        if (error) {
          this.changeErrorMsg(error.problemDetails);
          this.changeClaimOutcome(ClaimOutcome.Fail);
          return;
        }

        // Handle success
        this.changeClaimOutcome(ClaimOutcome.Success);
        this.changeIsInit(false);
        StorageHelper.GetInstance.debounceStorageArea('req', CacheAreaKey.Rewards);

        AnalyticsEventStore.addEvent(AnalyticsJourneyKey.Rewards, AnalyticsEventJourneyKey.RewardServiceSuccess, {
          context: `Successful opt ${optIn ? 'in' : 'out'} for rewards`,
          serviceName: 'reward_service',
          productName: this.itemDetails.id,
          url: Util.CurrentLocation,
        });
      })
      .catch((error) => {
        // Handle errors, including network issues or failed requests
        console.error(error);
        this.changeErrorMsg('An error occurred during the request.');
        this.changeClaimOutcome(ClaimOutcome.Fail);

        AnalyticsEventStore.addEvent(AnalyticsJourneyKey.Rewards, AnalyticsEventJourneyKey.RewardServiceFailure, {
          context: 'An error occurred during the request.',
          serviceName: 'reward_service',
          productName: this.itemDetails.id,
          url: Util.CurrentLocation,
        });
      })
      .finally(() => {
        this.changeIsLoadingClaim(false);
      });
  }

  // CLAIM REWARDS, ENTER COMPETITION AND ADD COMPETITION ENTRIES
  claimEvent(eventType: ClaimCard) {
    this.changeIsLoadingClaim(true);

    const link = this.itemDetails?.description?.actionLink?.url ?? '';

    if (link.trim().length > 1) {
      window.open(this.itemDetails?.description?.actionLink?.url, '_blank');
      this.changeIsLoadingClaim(false);
      return;
    }

    const { customerNumber } = this.queryParams;
    const agreementId = this.itemDetails.requiresAgreement ? { agreementId: this.agreementId.toString() } : {};
    const isCompetition = eventType === ClaimCard.Competition;

    const rewardBodyParams = { rewardId: this.itemDetails.id };
    const competitionBodyParams = {
      rewardId: this.competitionReward.rewardId,
      competitionName: this.itemDetails.id,
      count: this.noOfEntries,
    };
    const claimRequestBody = isCompetition ? competitionBodyParams : rewardBodyParams;
    RedOxygenService.claim({ customerNumber, ...agreementId, ...claimRequestBody })
      .then((data) => {
        const error = this.getError(data);
        if (error) {
          this.changeErrorMsg(error.problemDetails);
          this.changeClaimOutcome(ClaimOutcome.Fail);
          AnalyticsEventStore.addEvent(AnalyticsJourneyKey.Rewards, AnalyticsEventJourneyKey.RewardServiceFailure, {
            context: `Failure to claim ${this.itemDetails.type}`,
            serviceName: 'reward_service',
            productName: this.itemDetails.id,
            url: Util.CurrentLocation,
          });
          return;
        }
        AnalyticsEventStore.addEvent(AnalyticsJourneyKey.Rewards, AnalyticsEventJourneyKey.RewardServiceSuccess, {
          context: `Successful claim ${this.itemDetails.type}`,
          serviceName: 'reward_service',
          productName: this.itemDetails.id,
          url: Util.CurrentLocation,
        });
        this.changeClaimOutcome(ClaimOutcome.Success);
        debounceRewardsStorageArea();
      })
      .finally(() => {
        this.changeIsLoadingClaim(false);
      });
  }

  // Check for specific conditions indicating error (Note: RO returns errors in a 200 response)
  getError(data: RedOxygenErrorResponse | RedOxygenGenericResponse) {
    return !!('problemDetails' in data && !data.ok) ? data : null;
  }

  getAnyError(data: (RedOxygenErrorResponse | RedOxygenGenericResponse)[]): RedOxygenErrorResponse | undefined {
    return data.find((res) => this.getError(res)) as RedOxygenErrorResponse;
  }

  getRewardItemFromID(rewardId: string) {
    return this.claimableRewards.find((reward) => reward.id === rewardId);
  }

  //SETTERS
  changeRewardsLevel(starsCount: number) {
    this.rewardsLevel = RewardUtils.GetRewardsLevel(starsCount);
  }

  changeNextLevel(starsCount: number) {
    this.nextLevel = RewardUtils.GetRewardsNextLevel(starsCount);
  }

  changeCompEntries(_value: number) {
    this.compEntries = _value;
  }

  changeCompetitionReward(reward: RewardBase) {
    this.competitionReward = reward;
  }

  changeCurrencyStore(currencyStore: CurrencyStore) {
    this.currencyStore = currencyStore;
  }

  changeClaimableRewards(rewards: RewardCard[]) {
    this.claimableRewards = rewards.map((reward) => {
      return { ...reward, isActive: true, button: { text: 'buttons.claimNow' } };
    });
  }

  changeClaimableRewardsLocked(rewards: RewardCard[]) {
    this.claimableRewardsLocked = rewards.map((reward) => {
      return { ...reward, icon: 'icon-rewards_unlock', isActive: false, button: { text: 'buttons.unlock' } };
    });
  }

  changeEarnedStars(data: Partial<RewardCard>[]) {
    this.earnedStars = data;
  }

  changeEarnMoreStars(data: Partial<RewardCard>[]) {
    this.earnMoreStars = data;
  }

  changeStarStatBase(data: StarCategories) {
    this.starStatBase = data;
  }

  changeIsDetailsWindowOpen(isOpen: boolean) {
    if (!isOpen) {
      this.changeClaimOutcome(ClaimOutcome.None);
      this.changeNoOfEntries('');
    }
    this.isDetailsWindowOpen = isOpen;
  }

  changeIsInit(isInit: boolean) {
    this.isInit = isInit;
  }

  changeIsInitContent(isInit: boolean) {
    this.isInitContent = isInit;
  }

  changeIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  changeIsLoadingClaim(isLoading: boolean) {
    this.isLoadingClaim = isLoading;
  }

  changeItemDetails(item: RewardCard) {
    this.itemDetails = item;
  }

  changeNoOfEntries(val: string) {
    this.noOfEntries = val;
  }

  changePrincipalPackage(pkgName: string) {
    this.principalPackage = pkgName.replace(/dstv/gi, '').trim();
  }

  changeQueryParams(qp: RewardQueryParams) {
    this.queryParams = qp;
  }

  changeRewardsClaimedNumber(_value: number) {
    this.rewardsClaimed = _value;
  }

  changeRewardCompetitionCards(competitions: RewardCard[]) {
    this.rewardCompetitionCards = competitions.map((comp) => {
      return {
        ...comp,
        isActive: true,
        button: {
          text: 'buttons.earn',
        },
      };
    });
  }

  changeRewardHistoryCardsAll(cards: RewardCard[]) {
    this.rewardHistoryCardsAll = cards;
    this.changeRewardHistoryCardsMissed(cards.filter((card) => card.status === RewardStatus.Expired));
    this.changeRewardHistoryCardsEntered(
      cards.filter((card) => card.entryDate && card.status !== RewardStatus.Expired)
    );
  }

  changeRewardHistoryCardsEntered(cards: RewardCard[]) {
    this.rewardHistoryCardsEntered = cards.map((card) => {
      return {
        ...card,
        description: {
          ...card.description,
          endDate: new Date(this.competitionReward.expiryDate),
        },
        cardType: ClaimCard.Competition,
      };
    });
  }

  changeRewardHistoryCardsClaimed(cards: RewardCard[]) {
    this.rewardHistoryCardsClaimed = cards.map((card) => {
      return {
        ...card,
        cardType: ClaimCard.Reward,
      };
    });
  }

  changeRewardHistoryCardsMissed(cards: RewardCard[]) {
    this.rewardHistoryCardsMissed = cards;
  }

  changeRewardTypes(rewards: RewardCard[]) {
    this.rewardTypes = rewards;
  }

  changeStarsCount(_value: number) {
    this.starsCount = _value;
  }

  changeEarnedStarsStats(stats: StarCalc) {
    this.earnedStarsStats = stats;
  }

  changeMaxStarsStats(stats: StarCalc) {
    this.maxStarsStats = stats;
  }

  changeValue(_value: number) {
    this.value = _value;
  }

  changeWinnersCircle(data: CompletedCompetition[]) {
    const categories = data.map((x) => x.category);
    const uniqueCategories = [...new Set(categories)];

    this.competitionCategories = uniqueCategories;
    this.winnersCircle = data;
  }

  changeIsJoined(optin: string | boolean) {
    this.isJoined = optin === 'True' || optin === true;
  }

  changeClaimOutcome(outcome: ClaimOutcome) {
    this.claimOutcome = outcome;
  }

  changeErrorMsg(message: string | null | undefined) {
    this.errorMsg = message;
  }

  changeHasError(hasError: boolean) {
    this.hasError = hasError;
  }

  changeAgreementId(id: number) {
    this.agreementId = id;
  }

  increaseStars(_value: number) {
    this.starsCount = this.starsCount + _value;
  }

  changeNumberOfCoins(_value: number) {
    this.numberOfCoins = _value;
  }

  changeLandingPageContent(_value: LandingPageContent) {
    this.landingPageContent = _value;
  }

  changeCompetitionsContent(_value: ContentWithImage[]) {
    this.competitionsContent = _value;
  }

  changeWinnersCircleContent(_value: WinnersCircleContent) {
    this.winnersCircleContent = _value;
  }

  changeCompetitionCategory(_value: string) {
    this.competitionCategory = _value;
  }

  changeIsPremiumExperienceTier(_value: boolean) {
    this.isPremiumExperienceTier = _value;
  }
}